import React, { useState } from 'react'
import { Menu, MessageCircle, Sun, Moon, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useThemeContext } from '../../contexts/ThemeContext'
import LanguageSelector from '../Layout/LanguageSelector'
import ChangeContextDialog from '../ChangeContextDialog'
import { HEADER_LEFT_PADDING_PX, HEADER_RIGHT_PADDING_PX, HEADER_HEIGHT_PX } from '../../constants/layout'

interface HeaderProps {
    open: boolean
    setOpen: (open: boolean) => void
}

/**
 * Títulos das telas para o header (padrão: PLANO-VISUAL 4.4).
 * Novas rotas: adicionar aqui ou usar fallback por pathname.
 */
const getPageTitle = (pathname: string, t: (key: string) => string): string => {
    const map: Record<string, string> = {
        '/': '',
        '/grupos': t('groups.title'),
        '/empresas': t('companies.title'),
        '/usuarios': t('menu.users'),
        '/sessoes': t('menu.sessions'),
        '/logs': t('menu.logs'),
        '/audit-log': 'Audit Log',
        '/log-erros': 'Error log',
        '/backups': 'Configurações de Backup',
        '/areas-funcionais': 'Áreas Funcionais',
        '/usuarios/manutencao': 'Manutenção de Usuários',
        '/sistema-galatea': t('menu.systemGalatea'),
        '/telas': 'Gerenciar Telas (Global)',
        '/telas/empresa': 'Gerenciar Telas (Empresa)',
        '/thorjava/menu': 'Menu ThorJava',
        '/config': t('menu.config'),
        '/config/dicionariothor': 'Dicionário Thor',
        '/config/dicionario-dados': 'Dicionário de Dados',
        '/fluxos': 'Fluxos',
        '/telas-root': 'Tabelas',
    }
    if (map[pathname]) return map[pathname]
    if (pathname.startsWith('/grupos/')) return t('groups.title')
    if (pathname.startsWith('/empresas/')) return t('companies.title')
    if (pathname.startsWith('/usuarios/') && pathname !== '/usuarios/manutencao') return t('menu.users')
    if (pathname.startsWith('/config/')) return t('menu.config')
    if (pathname === '/basicas') return 'Tabelas Básicas'
    if (pathname === '/funcionais') return 'Tabelas Funcionais'
    if (pathname === '/reservadas') return 'Tabelas Reservadas'
    // Rotas dinâmicas: usar primeiro segmento capitalizado como fallback
    const segment = pathname.replace(/^\//, '').split('/')[0]
    if (segment) {
        const name = segment.replace(/-/g, ' ')
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
    }
    // No primeiro acesso (rota /dashboard ou raiz), não exibir título "Dashboard"
    if (pathname === '/dashboard' || pathname === '/') return ''
    
    return t('menu.dashboard')
}

const ModernHeader: React.FC<HeaderProps> = ({ open, setOpen }) => {
    const { t } = useTranslation()
    const location = useLocation()
    const { user, logout, getUserContext } = useAuth()
    const { mode, toggleMode } = useThemeContext()
    const navigate = useNavigate()
    const [changeContextDialogOpen, setChangeContextDialogOpen] = useState(false)
    const userContext = getUserContext()
    
    // Dados da Empresa (Razão Social / Nome e CNPJ)
    const empresaRazaoSocial: string | null = (userContext as any)?.empresa_razao_social || (userContext as any)?.empresa_nome || null
    const empresaCnpj: string | null = (userContext as any)?.empresa_cnpj || null
    const empresaInicial = empresaRazaoSocial ? empresaRazaoSocial.charAt(0).toUpperCase() : 'E'
    
    const pageTitle = getPageTitle(location.pathname, t)

    const handleLogout = async () => {
        try {
            await logout()
            navigate('/login')
        } catch (error) {
            console.error('Erro ao deslogar:', error)
            // Forçar redirecionamento mesmo se der erro
            navigate('/login')
        }
    }

    const formatCnpj = (value: string | null): string | null => {
        if (!value) return null
        const digits = value.replace(/\D/g, '')
        if (digits.length !== 14) return value
        return `${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,12)}-${digits.slice(12)}`
    }

    const sidebarWidthPx = open ? 256 : 64
    return (
        <header
            className="fixed top-0 right-0 z-30 flex items-center justify-between border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/90 backdrop-blur transition-all duration-300"
            style={{
                height: HEADER_HEIGHT_PX,
                left: sidebarWidthPx,
                paddingLeft: HEADER_LEFT_PADDING_PX,
                paddingRight: HEADER_RIGHT_PADDING_PX,
                width: `calc(100% - ${sidebarWidthPx}px)`,
            }}
        >
            <div className="flex items-center gap-4">
                <button
                    onClick={() => setOpen(!open)}
                    className="rounded-lg p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
                >
                    <Menu size={20} />
                </button>
                
                {/* Logo e Nome da Empresa no Header */}
                {(empresaRazaoSocial || empresaCnpj) && (
                    <div className="flex items-center gap-3">
                        {/* Mock Logo da Empresa */}
                        <div className="flex-shrink-0 h-10 w-10 rounded bg-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                            {empresaInicial}
                        </div>
                        
                        <div className="flex flex-col leading-tight min-w-0">
                            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[300px]" title={empresaRazaoSocial || ''}>
                                {empresaRazaoSocial || ''}
                            </span>
                            {empresaCnpj && (
                                <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                    {formatCnpj(empresaCnpj)}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex-1 flex justify-center items-center min-w-0 px-2">
                <span className="text-xl font-semibold text-slate-700 dark:text-slate-200 truncate text-center hidden md:block">
                    {pageTitle}
                </span>
            </div>

            <div className="flex items-center gap-2 md:gap-4 shrink-0">


                <LanguageSelector />

                <button
                    onClick={toggleMode}
                    className="rounded-lg p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                    title={mode === 'dark' ? t('theme.light') : t('theme.dark')}
                >
                    {mode === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                </button>

                {/* Avisos: ícone de balão (speech bubble) como em produção */}
                <button className="rounded-lg p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 relative" title={t('common.notifications')}>
                    <MessageCircle size={20} />
                    <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
                </button>

                <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>

                <div className="flex items-center gap-3">
                    <div className="hidden md:block text-right">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{user?.name || t('common.user')}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
                    </div>
                    <div className="h-9 w-9 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center text-blue-600 dark:text-blue-300 font-medium">
                        {user?.name?.charAt(0) || 'U'}
                    </div>

                    {/* Sair: texto visível como em produção */}
                    <button
                        onClick={handleLogout}
                        title={t('auth.logout')}
                        className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-slate-600 dark:text-slate-300 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 transition-colors font-medium"
                    >
                        <LogOut size={18} />
                        <span>{t('auth.logout')}</span>
                    </button>
                </div>
            </div>

            <ChangeContextDialog
                open={changeContextDialogOpen}
                onClose={() => setChangeContextDialogOpen(false)}
            />
        </header>
    )
}

export default ModernHeader
